import { Suspense, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { setupRefreshTokenInterceptor } from "./services/Interceptors";
import { AdminHeader, HeadersContainer, MainHeader } from "@/components/molecules/Header";
import { useAuth } from "@/contexts/AuthContext";
import { RedirectionTypeEnum } from "@/constants/enums/RedirectionTypeEnum";
import { getRedirection, getToken } from "@/utils/sessionStorage";
import { getRuntimeConfig } from "@/utils/runtimeConfig";
import Dashboard from "@/components/pages/Dashboard";
import LoginCheckEmail from "@/components/pages/Login/LoginCheckEmail";
import ExternalAuthSession from "@/components/pages/Login/ExternalAuthSession";
import Contracts from "@/components/pages/Contracts/";
import Statistics from "@/components/pages/Statistics";
import Settings from "@/components/pages/Settings";
import ProtectedRoute from "@/components/atoms/ProtectedRoute/ProtectedRoute";
import StaleTransactions from "@/archived/StaleTransactions/StaleTransactions";
import AdminFeedback from "@/archived/AdminFeedback/AdminFeedback";
import Tasks from "@/components/pages/Tasks";
import MyOrders from "@/components/pages/MyOrders";
import Inspections from "@/components/pages/Inspections";
import InvoiceArchive from "@/components/pages/InvoiceArchive";
import MatchWeightReports from "@/components/pages/MatchWeightReports";
import Footer from "@/components/molecules/Footer";
import ContractDetails from "@/components/pages/ContractDetails";
import ClaimsAdmin from "@/components/pages/ClaimsAdmin";
import AppContent from "@/components/templates/AppContent";
import CompanyApplicationAdmin from "@/components/pages/CompanyAdmin";

import Spinner from "@/components/atoms/Spinner/Spinner";
import RedirectLoadingScreen from "@/components/pages/RedirectLoadingScreen";
import RegisterCompanyWizard from "@/components/pages/RegisterCompany";
import ReportsAdmin from "@/components/pages/ReportsAdmin";
import Market from "@/components/pages/Market";
import OrderDetails from "@/components/pages/OrderDetails";
import LogisticsManager from "@/components/pages/LogisticsManager";

import "./styles/App.css";
import CreateContract from "@/components/organisms/CreateContract";
import Reports from "@/components/pages/Reports";
import AccountGate from "@/components/organisms/AccountGate";
import CreateOrderFromContract from "@/components/organisms/CreateOrderFromContract";
import { useCreateOrderFromContractStore } from "./stores/createOrderFromContractStore";
import DrawerOrders from "@/components/molecules/DrawerOrders";
import InspectionModal from "@/components/organisms/InspectionModal";
import { useInspectionModalStore } from "./stores/inspectionModalStore";
import { useCreateContractWizardStore } from "./stores/createContractWizardStore";
import { useBranding } from "@/contexts/BrandingContext";
import { useWhiteLabelContext } from "@/services/WhiteLabel";
import TradeLockBanner from "@/components/molecules/TradeLockBanner";

function parseCallbackUrl(url: string) {
  return new URLSearchParams(url);
}

function App() {
  const { signOut, isUserAdmin, isCurrentCompanyAdmin, companies } = useAuth();
  const { setBranding } = useBranding();

  const { branding: whiteLabelBranding } = useWhiteLabelContext();

  useEffect(() => {
    if (!whiteLabelBranding) return;
    const { primaryColor, borderRadius } = whiteLabelBranding;

    if (primaryColor != null || borderRadius != null) {
      setBranding({ primaryColor: primaryColor ?? undefined, borderRadius: borderRadius ?? undefined });
    }
  }, [whiteLabelBranding]);

  const location = useLocation();
  const params = parseCallbackUrl(window.location.search);
  const code = params.get("code");
  const isOrderModalOpen = useCreateOrderFromContractStore((s) => s.isOpen);
  const isContractModalOpen = useCreateContractWizardStore((s) => s.isOpen);
  const isInspectionModalOpen = useInspectionModalStore((s) => s.isOpen);

  const isSettings = location.pathname.split("/")[1] === "settings";
  const isGraySection = ["task", "myOrders", "inspections", "invoiceArchive", "matchWeightReports"].includes(
    location.pathname.split("/")[1]
  );

  useEffect(() => {
    document.body.classList.toggle("admin", isCurrentCompanyAdmin);
    document.body.classList.toggle("settings", isSettings);
    document.body.classList.toggle("graySection", isGraySection);
  }, [isCurrentCompanyAdmin, isSettings, isGraySection]);

  useEffect(() => {
    // Install once at mount so 401s on the very first query (e.g. reload with an
    // expired token, before currentCompany has loaded) trigger logout/redirect.
    setupRefreshTokenInterceptor(signOut);
  }, [signOut]);

  const buildVersion = getRuntimeConfig("BUILD_VERSION");

  const redirection = getRedirection();
  const token = getToken();

  if (location.pathname === "/external-redirect") {
    return <RedirectLoadingScreen />;
  }

  if (redirection === RedirectionTypeEnum.CHECK_EMAIL) {
    return <LoginCheckEmail />;
  }

  if (redirection === RedirectionTypeEnum.EXTERNAL && code) {
    return <ExternalAuthSession code={code as string} />;
  }

  // The authenticated shell is gated on an actual token, not just the presence of
  // a `redirection` value. `redirection` is set when the user requests a one-time
  // code, so without this check anyone who started the OTP flow and then revisited
  // the base URL (without `?code=`) would land inside the app shell.
  if (!token) {
    return (
      <Routes>
        <Route path="/login" element={<LoginCheckEmail />} />
        <Route path="/" element={<LoginCheckEmail />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  } else if (redirection === RedirectionTypeEnum.CHECK_EMAIL) {
    return <LoginCheckEmail />;
  } else if (redirection === RedirectionTypeEnum.EXTERNAL && code) {
    return <ExternalAuthSession code={code as string} />;
  } else {
    return (
      <>
        <Suspense fallback={<Spinner loading={true} />}>
          <meta name="build-version" content={buildVersion} />
          <link rel="canonical" href="https://reviver.no" />
          <HeadersContainer>
            {isCurrentCompanyAdmin && isUserAdmin && (
              <>
                <AdminHeader />
              </>
            )}
            <MainHeader />
            <TradeLockBanner />
          </HeadersContainer>
          <AccountGate>
            <AppContent>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route
                  path="/registerCompany"
                  element={companies.length > 0 ? <Navigate to="/dashboard" replace /> : <RegisterCompanyWizard />}
                />
                <Route path="/contracts/details/:contractId" element={<ContractDetails />} />
                <Route path="/contracts" element={<Contracts />} />
                <Route path="/settings/*" element={<Settings />} />
                <Route path="/task" element={<Tasks />} />
                <Route path={"/invoiceArchive"} element={<InvoiceArchive />} />
                <Route path="/myOrders" element={<MyOrders />} />
                <Route path="/inspections" element={<Inspections />} />
                <Route path="/containers/*" element={<LogisticsManager />} />
                <Route path="/reports" element={<Reports />} />
                {getRuntimeConfig("ENABLED_MARKET") === "true" && <Route path="/market" element={<Market />} />}
                <Route path="/order/details/:orderId" element={<OrderDetails />} />
                <Route path="/matchWeightReports" element={<MatchWeightReports />} />

                <Route
                  path="/adminCompanies"
                  element={
                    <ProtectedRoute isAdmin={isCurrentCompanyAdmin}>
                      <CompanyApplicationAdmin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/statistics"
                  element={
                    <ProtectedRoute isAdmin={isCurrentCompanyAdmin}>
                      <Statistics />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/matchWeightReports"
                  element={
                    <ProtectedRoute isAdmin={isCurrentCompanyAdmin}>
                      <MatchWeightReports />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/staleTransactions"
                  element={
                    <ProtectedRoute isAdmin={isCurrentCompanyAdmin}>
                      <StaleTransactions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adminFeedback"
                  element={
                    <ProtectedRoute isAdmin={isCurrentCompanyAdmin}>
                      <AdminFeedback />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adminClaims"
                  element={
                    <ProtectedRoute isAdmin={isCurrentCompanyAdmin}>
                      <ClaimsAdmin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adminReports"
                  element={
                    <ProtectedRoute isAdmin={isCurrentCompanyAdmin}>
                      <ReportsAdmin />
                    </ProtectedRoute>
                  }
                />
              </Routes>
              {isContractModalOpen && <CreateContract />}
              {isOrderModalOpen && <CreateOrderFromContract />}
              {isInspectionModalOpen && <InspectionModal />}
              <DrawerOrders />
            </AppContent>
          </AccountGate>
          <Footer />
        </Suspense>
      </>
    );
  }
}

export default App;
